import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import apiService from '../services/api'
import Card from '../components/ui/Card'
import { useIcon } from '../hooks/useIcons'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { useAuth } from '../contexts/AuthContext'

const Timetable = () => {
  const { t } = useTranslation()
  const { user, logout, refreshUser } = useAuth()
  const [prayerTimes, setPrayerTimes] = useState(null)
  const [currentWakt, setCurrentWakt] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [districts, setDistricts] = useState([])
  const [countries, setCountries] = useState([])
  const [selectedDistrict, setSelectedDistrict] = useState('Dhaka')
  const [selectedCountry, setSelectedCountry] = useState('Bangladesh')
  const [selectedAsrMethod, setSelectedAsrMethod] = useState(1) // 0=Standard, 1=Hanafi (default)
  const [savingLocation, setSavingLocation] = useState(false)
  const [savingAsrMethod, setSavingAsrMethod] = useState(false)

  const ClockIcon = useIcon('Clock')
  const SunriseIcon = useIcon('Sunrise')
  const SunsetIcon = useIcon('Sunset')
  const MoonIcon = useIcon('Moon')
  const LoaderIcon = useIcon('Loader')

  // Prayer time icons mapping
  const getPrayerIcon = (prayerName) => {
    const iconMap = {
      'Fajr': '🌙',
      'Sunrise': '🌅',
      'Ishraq': '☀️',
      'Midday': '🌞',
      'Dhuhr': '☀️',
      'Asr': '🌤️',
      'Maghrib': '🌆',
      'Isha': '🌙',
      'Night': '🌃',
      'Imsak': '🌑',
      'Midnight': '🌃'
    }
    return iconMap[prayerName] || '🕌'
  }

  // Calculate time remaining with seconds
  const calculateTimeRemaining = () => {
    if (!currentWakt || !prayerTimes) return null

    const now = new Date()
    const currentMinutes = now.getHours() * 60 + now.getMinutes()
    const currentSeconds = now.getSeconds()

    // Parse next wakt time
    const [nextHours, nextMinutes] = currentWakt.next_wakt_time.split(':').map(Number)
    let nextWaktMinutes = nextHours * 60 + nextMinutes

    // Handle crossing midnight (if next wakt is tomorrow's Fajr)
    if (nextWaktMinutes <= currentMinutes && currentWakt.next_wakt === 'Fajr') {
      nextWaktMinutes += 24 * 60
    }

    // Calculate total seconds remaining
    const minutesRemaining = nextWaktMinutes - currentMinutes
    const totalSecondsRemaining = (minutesRemaining * 60) - currentSeconds

    // Show zero if time is negative (wakt ended but API not updated yet)
    if (totalSecondsRemaining < 0) {
      return '00:00:00'
    }

    const hours = Math.floor(totalSecondsRemaining / 3600)
    const minutes = Math.floor((totalSecondsRemaining % 3600) / 60)
    const seconds = totalSecondsRemaining % 60

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  // Fetch districts and countries on mount
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await apiService.getDistricts()
        if (response.success) {
          setDistricts(response.data.districts)
          setCountries(response.data.countries)
        }
      } catch (err) {
        console.error('Failed to fetch districts:', err)
      }
    }
    fetchDistricts()
  }, [])

  // Load user's saved preferences if logged in
  useEffect(() => {
    if (user && user.id) {
      // Load district and country
      if (user.prayer_time_district) {
        setSelectedDistrict(user.prayer_time_district)
      }
      if (user.prayer_time_country) {
        setSelectedCountry(user.prayer_time_country)
      }
      // Load Asr calculation method (could be 0 or 1, so check for undefined/null)
      if (user.asr_calculation_method !== undefined && user.asr_calculation_method !== null) {
        setSelectedAsrMethod(user.asr_calculation_method)
      }
      console.log('📍 Loaded user preferences:', {
        district: user.prayer_time_district,
        country: user.prayer_time_country,
        asrMethod: user.asr_calculation_method
      })
    }
  }, [user])

  // Fetch prayer times when district/country/asr method changes
  useEffect(() => {
    const fetchPrayerTimes = async () => {
      try {
        setLoading(true)
        const response = await apiService.getPrayerTimesToday(selectedDistrict, selectedCountry, selectedAsrMethod)
        if (response.success) {
          setPrayerTimes(response.data)
        } else {
          setError('Failed to load prayer times')
        }
      } catch (err) {
        setError(err.message || 'Failed to load prayer times')
      } finally {
        setLoading(false)
      }
    }

    fetchPrayerTimes()
  }, [selectedDistrict, selectedCountry, selectedAsrMethod])

  // Fetch current wakt when district/country/asr method changes
  useEffect(() => {
    const fetchCurrentWakt = async () => {
      try {
        const response = await apiService.getCurrentWakt(selectedDistrict, selectedCountry, selectedAsrMethod)
        if (response.success) {
          setCurrentWakt(response.data)
        }
      } catch (err) {
        console.error('Failed to fetch current wakt:', err)
      }
    }

    fetchCurrentWakt()
    // Refresh every minute
    const interval = setInterval(fetchCurrentWakt, 60000)
    return () => clearInterval(interval)
  }, [selectedDistrict, selectedCountry, selectedAsrMethod])

  // Handle location change
  const handleLocationChange = async (district, country) => {
    console.log('🌍 Changing location to:', district, country)
    setSelectedDistrict(district)
    setSelectedCountry(country)

    // Save to user profile if logged in
    if (user) {
      try {
        setSavingLocation(true)
        console.log('💾 Saving location to backend...')
        await apiService.updatePrayerLocation(district, country)
        // Refresh user profile to persist changes
        console.log('🔄 Refreshing user profile...')
        await refreshUser()
        console.log('✅ Location saved and profile refreshed')
      } catch (err) {
        console.error('❌ Failed to save prayer location:', err)
      } finally {
        setSavingLocation(false)
      }
    }
  }

  // Handle Asr calculation method change
  const handleAsrMethodChange = async (method) => {
    const methodInt = parseInt(method)
    console.log('🕌 Changing Asr method to:', methodInt, methodInt === 0 ? 'Standard' : 'Hanafi')
    setSelectedAsrMethod(methodInt)

    // Save to user profile if logged in
    if (user) {
      try {
        setSavingAsrMethod(true)
        console.log('💾 Saving Asr method to backend...')
        await apiService.updateAsrCalculationMethod(methodInt)
        // Refresh user profile to persist changes
        console.log('🔄 Refreshing user profile...')
        await refreshUser()
        console.log('✅ Asr method saved and profile refreshed')
      } catch (err) {
        console.error('❌ Failed to save Asr calculation method:', err)
      } finally {
        setSavingAsrMethod(false)
      }
    }
  }

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  // Format time to 12-hour format
  const formatTime12Hour = (time24) => {
    if (!time24) return '--:--'
    const [hours, minutes] = time24.split(':').map(Number)
    const period = hours >= 12 ? 'PM' : 'AM'
    const hours12 = hours % 12 || 12
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoaderIcon className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-3 text-lg">{t('common.loading')}</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="bg-red-50 border-red-200">
          <p className="text-red-600">{error}</p>
        </Card>
      </div>
    )
  }

  if (!prayerTimes) {
    return null
  }

  return (
    <>
      <Navbar user={user} onLogout={logout} />
      <div className="container mx-auto px-4 py-6 max-w-4xl">
      {/* Header with Date */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-center mb-2 bg-gradient-primary bg-clip-text text-transparent">
          {t('timetable.title')}
        </h1>

        {/* Location Selection Dropdowns */}
        <div className="flex flex-col md:flex-row gap-3 justify-center items-center mb-4">
          <div className="flex flex-row gap-2 items-center w-full md:w-auto">
            <label className="text-sm font-medium text-muted-foreground whitespace-nowrap">District:</label>
            <select
              value={selectedDistrict}
              onChange={(e) => handleLocationChange(e.target.value, selectedCountry)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white text-sm flex-1"
              disabled={savingLocation}
            >
              {districts.map(district => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-row gap-2 items-center w-full md:w-auto">
            <label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Country:</label>
            <select
              value={selectedCountry}
              onChange={(e) => handleLocationChange(selectedDistrict, e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white text-sm flex-1"
              disabled={savingLocation}
            >
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>

          {savingLocation && (
            <span className="text-xs text-primary">Saving...</span>
          )}
        </div>

        {/* Asr Calculation Method Dropdown */}
        <div className="flex justify-center items-center mb-4 px-4">
          <div className="flex flex-row gap-2 items-center w-full md:w-auto">
            <label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Madhhab:</label>
            <select
              value={selectedAsrMethod}
              onChange={(e) => handleAsrMethodChange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white text-sm flex-1"
              disabled={savingAsrMethod}
            >
              <option value={0}>Standard (Shafi, Maliki, Hanbali)</option>
              <option value={1}>Hanafi</option>
            </select>
            {savingAsrMethod && (
              <span className="text-xs text-primary">Saving...</span>
            )}
          </div>
        </div>

        {/* Gregorian Date */}
        <div className="text-center text-muted-foreground mb-2">
          <ClockIcon className="inline w-4 h-4 mr-2" />
          <span className="text-lg">
            {prayerTimes.gregorian?.weekday}, {prayerTimes.gregorian?.date}
          </span>
          <span className="ml-4 text-xl font-mono">
            {currentTime.toLocaleTimeString('en-US', { hour12: false })}
          </span>
        </div>

        {/* Hijri Date (English) */}
        <div className="text-center">
          <div className="inline-block bg-accent px-4 py-2 rounded-lg">
            <MoonIcon className="inline w-4 h-4 mr-2" />
            <span className="text-sm">
              {prayerTimes.hijri?.day} {prayerTimes.hijri?.month_en} {prayerTimes.hijri?.year} AH
            </span>
          </div>
        </div>
      </div>

      {/* Current Wakt Card */}
      {currentWakt && (
        <Card className={`mb-6 ${
          currentWakt.current_wakt === 'Sunrise' || currentWakt.current_wakt === 'Midday'
            ? 'bg-gradient-to-br from-red-50 to-red-100/50 border-red-200'
            : 'bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20'
        }`}>
          <div className="text-center">
            <div className="text-sm text-muted-foreground mb-2">
              {t('timetable.currentWakt')}
            </div>
            <div className="text-4xl font-bold mb-2">
              {getPrayerIcon(currentWakt.current_wakt)} {currentWakt.current_wakt}
            </div>
            <div className="text-lg text-muted-foreground mb-4">
              {formatTime12Hour(currentWakt.current_wakt_start)}
            </div>

            {/* Countdown */}
            <div className="mb-4">
              <div className="text-sm text-muted-foreground mb-1">
                {t('timetable.timeRemaining')}
              </div>
              <div className="text-3xl font-mono font-bold text-primary">
                {calculateTimeRemaining()}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {t('timetable.until')} {currentWakt.next_wakt} • {formatTime12Hour(currentWakt.next_wakt_time)}
              </div>
            </div>

            {/* Enhanced Animated Progress Bar */}
            <div className="space-y-2">
              {/* Progress Bar Container with Shadow */}
              <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-6 overflow-hidden shadow-inner">
                {/* Remaining Time Fill - Decreases from right to left */}
                <div
                  className={`absolute right-0 top-0 h-full rounded-full transition-all duration-1000 ease-linear ${
                    currentWakt.time_remaining_seconds < 300
                      ? 'bg-gradient-to-l from-red-500 to-red-600 animate-pulse shadow-lg shadow-red-500/50'
                      : currentWakt.time_remaining_seconds < 900
                      ? 'bg-gradient-to-l from-orange-400 to-orange-500 shadow-md shadow-orange-400/30'
                      : currentWakt.time_remaining_seconds < 1800
                      ? 'bg-gradient-to-l from-yellow-400 to-yellow-500 shadow-md shadow-yellow-400/20'
                      : 'bg-gradient-to-l from-green-400 to-green-600 shadow-md shadow-green-400/20'
                  }`}
                  style={{
                    width: `${100 - currentWakt.progress_percentage}%`
                  }}
                >
                  {/* Animated Shimmer Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>

                  {/* Pulsing Glow for Low Time */}
                  {currentWakt.time_remaining_seconds < 300 && (
                    <div className="absolute inset-0 animate-pulse opacity-50"></div>
                  )}
                </div>
              </div>

              {/* Critical Time Warning */}
              {currentWakt.time_remaining_seconds < 600 && (
                <div className={`flex items-center justify-center gap-2 text-sm font-medium ${
                  currentWakt.time_remaining_seconds < 300
                    ? 'text-red-600 dark:text-red-400 animate-pulse'
                    : 'text-orange-600 dark:text-orange-400'
                }`}>
                  <svg className="w-4 h-4 animate-bounce" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>
                    {currentWakt.time_remaining_seconds < 300
                      ? '⏰ Less than 5 minutes remaining!'
                      : '⚠️ Less than 10 minutes remaining'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* All Prayer Times */}
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <span className="mr-2">🕌</span>
          {t('timetable.allPrayerTimes')}
        </h2>

        <div className="grid gap-3">
          {/* Fajr */}
          <Card className={`hover:shadow-md transition-shadow ${currentWakt?.current_wakt === 'Fajr' ? 'border-primary border-2' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{getPrayerIcon('Fajr')}</span>
                <div>
                  <div className="font-bold text-lg">{t('timetable.fajr')}</div>
                  <div className="text-sm text-muted-foreground">{t('timetable.fajrDesc')}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-mono font-bold">
                  {formatTime12Hour(prayerTimes.timings?.fajr)}
                </div>
              </div>
            </div>
          </Card>

          {/* Sunrise */}
          <Card className={`hover:shadow-md transition-shadow bg-red-50/50 ${currentWakt?.current_wakt === 'Sunrise' ? 'border-primary border-2' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{getPrayerIcon('Sunrise')}</span>
                <div>
                  <div className="font-bold text-lg">{t('timetable.sunrise')}</div>
                  <div className="text-sm text-muted-foreground">{t('timetable.sunriseDesc')}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-mono font-bold">
                  {formatTime12Hour(prayerTimes.timings?.sunrise)}
                </div>
              </div>
            </div>
          </Card>

          {/* Ishraq */}
          {prayerTimes.timings?.ishraq && (
            <Card className={`hover:shadow-md transition-shadow ${currentWakt?.current_wakt === 'Ishraq' ? 'border-primary border-2' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">☀️</span>
                  <div>
                    <div className="font-bold text-lg">{t('timetable.ishraq')}</div>
                    <div className="text-sm text-muted-foreground">{t('timetable.ishraqDesc')}</div>
                    <div className="text-xs text-gray-500 mt-1">Calculated: Sunrise + 15 min</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-mono font-bold">
                    {formatTime12Hour(prayerTimes.timings.ishraq)}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Midday (Zawal) */}
          {prayerTimes.timings?.midday && (
            <Card className={`hover:shadow-md transition-shadow bg-red-50/50 ${currentWakt?.current_wakt === 'Midday' ? 'border-primary border-2' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">🌞</span>
                  <div>
                    <div className="font-bold text-lg">{t('timetable.midday')}</div>
                    <div className="text-sm text-muted-foreground">{t('timetable.middayDesc')}</div>
                    <div className="text-xs text-gray-500 mt-1">Calculated: Dhuhr - 10 min</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-mono font-bold">
                    {formatTime12Hour(prayerTimes.timings.midday)}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Dhuhr */}
          <Card className={`hover:shadow-md transition-shadow ${currentWakt?.current_wakt === 'Dhuhr' ? 'border-primary border-2' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{getPrayerIcon('Dhuhr')}</span>
                <div>
                  <div className="font-bold text-lg">{t('timetable.dhuhr')}</div>
                  <div className="text-sm text-muted-foreground">{t('timetable.dhuhrDesc')}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-mono font-bold">
                  {formatTime12Hour(prayerTimes.timings?.dhuhr)}
                </div>
              </div>
            </div>
          </Card>

          {/* Asr */}
          <Card className={`hover:shadow-md transition-shadow ${currentWakt?.current_wakt === 'Asr' ? 'border-primary border-2' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{getPrayerIcon('Asr')}</span>
                <div>
                  <div className="font-bold text-lg">{t('timetable.asr')}</div>
                  <div className="text-sm text-muted-foreground">{t('timetable.asrDesc')}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-mono font-bold">
                  {formatTime12Hour(prayerTimes.timings?.asr)}
                </div>
              </div>
            </div>
          </Card>

          {/* Sunset */}
          <Card className={`hover:shadow-md transition-shadow ${currentWakt?.current_wakt === 'Sunset' ? 'border-primary border-2' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">🌇</span>
                <div>
                  <div className="font-bold text-lg">{t('timetable.sunset')}</div>
                  <div className="text-sm text-muted-foreground">{t('timetable.sunsetDesc')}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-mono font-bold">
                  {formatTime12Hour(prayerTimes.timings?.sunset)}
                </div>
              </div>
            </div>
          </Card>

          {/* Maghrib */}
          <Card className={`hover:shadow-md transition-shadow ${currentWakt?.current_wakt === 'Maghrib' ? 'border-primary border-2' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{getPrayerIcon('Maghrib')}</span>
                <div>
                  <div className="font-bold text-lg">{t('timetable.maghrib')}</div>
                  <div className="text-sm text-muted-foreground">{t('timetable.maghribDesc')}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-mono font-bold">
                  {formatTime12Hour(prayerTimes.timings?.maghrib)}
                </div>
              </div>
            </div>
          </Card>

          {/* Isha */}
          <Card className={`hover:shadow-md transition-shadow ${currentWakt?.current_wakt === 'Isha' ? 'border-primary border-2' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{getPrayerIcon('Isha')}</span>
                <div>
                  <div className="font-bold text-lg">{t('timetable.isha')}</div>
                  <div className="text-sm text-muted-foreground">{t('timetable.ishaDesc')}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-mono font-bold">
                  {formatTime12Hour(prayerTimes.timings?.isha)}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Special Islamic Timings */}
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <span className="mr-2">⭐</span>
          {t('timetable.specialTimings')}
        </h2>

        <div className="grid gap-3 sm:grid-cols-2">
          {/* Imsak */}
          {prayerTimes.timings?.imsak && (
            <Card className="hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">🌑</span>
                  <div>
                    <div className="font-bold text-lg">{t('timetable.imsak')}</div>
                    <div className="text-sm text-muted-foreground">{t('timetable.imsakDesc')}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-mono font-bold">
                    {formatTime12Hour(prayerTimes.timings.imsak)}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Last Third */}
          {prayerTimes.timings?.lastthird && (
            <Card className="hover:shadow-md transition-shadow border-2 border-amber-200 bg-amber-50/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">🌟</span>
                  <div>
                    <div className="font-bold text-lg">{t('timetable.lastThird')}</div>
                    <div className="text-sm text-muted-foreground">{t('timetable.lastThirdDesc')}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-mono font-bold">
                    {formatTime12Hour(prayerTimes.timings.lastthird)}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Midnight */}
          {prayerTimes.timings?.midnight && (
            <Card className="hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">🌃</span>
                  <div>
                    <div className="font-bold text-lg">{t('timetable.midnight')}</div>
                    <div className="text-sm text-muted-foreground">{t('timetable.midnightDesc')}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-mono font-bold">
                    {formatTime12Hour(prayerTimes.timings.midnight)}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* First Third */}
          {prayerTimes.timings?.firstthird && (
            <Card className="hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">🌙</span>
                  <div>
                    <div className="font-bold text-lg">{t('timetable.firstThird')}</div>
                    <div className="text-sm text-muted-foreground">{t('timetable.firstThirdDesc')}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-mono font-bold">
                    {formatTime12Hour(prayerTimes.timings.firstthird)}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Location and Method Info - Moved below timetable */}
      <div className="text-center text-sm text-muted-foreground mb-4 space-y-1">
        <p className="font-medium">{t('timetable.location')}: {selectedDistrict}, {selectedCountry}</p>
        {prayerTimes.method && (
          <p>
            {t('timetable.calculationMethod')}: {prayerTimes.method.name}
          </p>
        )}
        {prayerTimes.school && (
          <p>
            {t('timetable.asrCalculation')}: {prayerTimes.school === 'STANDARD' ? t('timetable.standardShafi') : t('timetable.hanafiMethod')}
          </p>
        )}
      </div>

      {/* Footer Note */}
      <div className="text-center text-xs text-muted-foreground mb-6">
        <p>{t('timetable.poweredBy')}: Al-Adhan API</p>
      </div>
      </div>
      <Footer />
    </>
  )
}

export default Timetable
